import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ExportQueryFn, useExportQuery } from '@givebacks/api/query';
import { hasKey } from '@givebacks/utils/objects';
import { InfiniteData } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ExportButton, Menu } from 'ui';

import { Column, columnsCreator, useCreateCsv } from '../../../../../src/utils';
import { useTableContext } from '../../../table.context';

const useTableExport = <T extends ExportQueryFn<any>>({
  exportQuery,
  columns,
}: {
  exportQuery: T;
  columns: Column<unknown>[];
}) => {
  const columnsRef = useRef(columns ?? []);
  const otfColumnsRef = useRef<Column<unknown>[]>([]);

  const ctx = useTableContext();

  const otfColumns = ctx.exportOptions?.otfColumnCreators;
  const querySettings = ctx.exportOptions?.settings;
  const isExportDisabled = ctx.exportOptions?.disabled;
  const isTableDataLoading = ctx.isLoading;
  const fileName = ctx.exportOptions?.fileName;

  const searchObject = useAtomValue(ctx.tableSearchObjectAtom, {
    store: ctx.atomStore,
  });

  const { download, setData, setColumns } = useCreateCsv({
    fileName: fileName ?? 'export',
    columns: columnsRef.current,
  });

  /* Adds any extra columns based on the data when provided with On the fly settings */
  const addOnTheFlyColumns = useCallback(
    <T,>(data?: T) => {
      if (!data) return;
      if (typeof data === 'object' && otfColumns?.length) {
        // Go through each OTF column and
        // See if that data key exists in the data set
        otfColumns.forEach(({ makeColumns, dataKey }) => {
          // Only process if the dataKey exists in the data
          if (hasKey(data, dataKey)) {
            const createdColumns = makeColumns(data, columnsCreator().create);
            // If the column is created, add it to the columns
            createdColumns.forEach((newColumn) => {
              const currentColumnKeys = otfColumnsRef.current.map((col) => col.header);
              if (newColumn && newColumn.header && !currentColumnKeys.includes(newColumn.header)) {
                otfColumnsRef.current.push(newColumn);
              }
            });
          } else {
            return;
          }
        });
      }
    },
    [otfColumns]
  );

  const parseData = useCallback(
    (page?: { data?: unknown[] }) => {
      const hasOtfSettings = otfColumns?.length;
      if (page?.data && page.data.length && hasOtfSettings) {
        page.data.forEach(addOnTheFlyColumns);
      }

      if (
        otfColumnsRef.current.length &&
        !otfColumnsRef.current.some((col) => col.header === 'Additional Fields')
      ) {
        otfColumnsRef.current.unshift({
          header: 'Additional Fields',
          cell: () => '',
        });
      }

      return page?.data;
    },
    [addOnTheFlyColumns, otfColumns?.length]
  );

  const formatDataReturn = useCallback(
    (data?: InfiniteData<any>) => data?.pages.flatMap((page) => parseData(page)) ?? [],
    [parseData]
  );

  const handleDownload = useCallback(
    (data?: InfiniteData<any>) => {
      setData(formatDataReturn(data));
      /**
       * Ensure the columns are updated to the latest ref prior to starting the download. This is because columns can change when the user changes the table settings or when the OTF columns are added.
       */
      setColumns([...columnsRef.current, ...otfColumnsRef.current]);
      download();
    },
    [download, formatDataReturn, setData, setColumns]
  );

  const { startExport, exporting, isFetching } = useExportQuery({
    useExportFn: exportQuery,
    queryOptions: searchObject,
    settings: querySettings,
    onComplete: handleDownload,
  });

  const startExportWithDownload = useCallback(() => {
    return startExport((data) => handleDownload(data));
  }, [handleDownload, startExport]);

  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  return {
    startExportWithDownload,
    exporting,
    isFetching,
    isTableDataLoading,
    isExportDisabled,
  };
};

const TableExportButton = ({
  exportQuery,
  columns,
}: {
  exportQuery: ExportQueryFn<any>;
  columns: Column<unknown>[];
}) => {
  const { startExportWithDownload, exporting, isFetching, isTableDataLoading, isExportDisabled } =
    useTableExport({
      exportQuery,
      columns,
    });

  return (
    <ExportButton
      onClick={startExportWithDownload}
      disabled={exporting || isTableDataLoading || isExportDisabled}
      loading={exporting || isFetching}
    />
  );
};

const TableCsvExportMenuItem = <T extends ExportQueryFn<any>>({
  exportQuery,
  columns,
  setIsLoading,
}: {
  exportQuery: T;
  columns: Column<unknown>[];
  setIsLoading?: (isLoading: boolean) => void;
}) => {
  const { startExportWithDownload, exporting, isFetching, isTableDataLoading, isExportDisabled } =
    useTableExport({
      exportQuery,
      columns,
    });

  const isLoading = useMemo(
    () => Boolean(exporting || isFetching || isTableDataLoading),
    [exporting, isFetching, isTableDataLoading]
  );

  useEffect(() => {
    setIsLoading?.(!!isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Don't run on fn change
  }, [isLoading]);

  return (
    <Menu.Item
      key={'csv-export'}
      onClick={(e) => {
        e.stopPropagation();
        return startExportWithDownload();
      }}
      disabled={isLoading || isExportDisabled}
    >
      Export as CSV
    </Menu.Item>
  );
};

type TableExportProps = {
  type: 'button' | 'menu-link';
  setIsLoading?: (isLoading: boolean) => void;
};

const TableCSVExport = ({ type, setIsLoading }: TableExportProps) => {
  const ctx = useTableContext();
  const exportQuery = ctx.exportOptions?.query;
  const exportColumns = ctx.exportOptions?.columns;
  const ExportButton = ctx.exportOptions?.Button;

  if (ExportButton) return <ExportButton />;
  if (!exportQuery || !exportColumns) return null;
  if (type == 'button') {
    return <TableExportButton exportQuery={exportQuery} columns={exportColumns} />;
  }
  if (type == 'menu-link') {
    return (
      <TableCsvExportMenuItem
        exportQuery={exportQuery}
        columns={exportColumns}
        setIsLoading={setIsLoading}
      />
    );
  }
};

export { TableCSVExport, TableCsvExportMenuItem };
